import { createContext, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSubmitQuiz } from "../features/web/quiz/hooks/useSubmitQuiz";
import { useContinueQuiz } from "../features/web/quiz/hooks/useContinueQuiz";
import { useLectureContent } from "../features/web/lecture/hooks/useLectureContent";
import Loader from "../components/Loader";

const QuizContext = createContext();

function QuizProvider({ children }) {
    const [visitedNumber, setVisitedNumber] = useState(1);
    const [visitedIterator, setVisitedIterator] = useState(() => visitedNumber);
    const [notAnsweredNumber, setNotAnsweredNumber] = useState(0);
    const [answeredNumber, setAnsweredNumber] = useState(0);
    const [currentQuestionNumber, setCurrentQuestionNumber] = useState(0);
    const [seenQuestions, setSeenQuestions] = useState(() => {
        const savedQuestions = localStorage.getItem("seenQuestions");
        return savedQuestions ? JSON.parse(savedQuestions) : [1];
    });

    const { courseId, lectureId, quizId, attemptId } = useParams();
    const { isPending: quizDetailLoading, lectureContent: quizDetail } =
        useLectureContent({
            courseId,
            lectureId,
            apiOperations: {
                filter: { field: "id", value: quizId },
            },
        });
    const { submitQuiz, isPending: isSubmitting } = useSubmitQuiz();

    const { isPending, quizAttemptData, error } = useContinueQuiz({
        courseId,
        lectureId,
        quizId,
        attemptId,
    });

    useEffect(() => {
        localStorage.setItem("seenQuestions", JSON.stringify(seenQuestions));
    }, [seenQuestions]);

    useEffect(() => {
        if (visitedIterator > visitedNumber) {
            setVisitedNumber((visitedNumber) => visitedNumber + 1);
        }
    }, [visitedIterator, visitedNumber]);

    if (
        isPending ||
        quizDetailLoading ||
        error?.response?.status === 401 ||
        error?.response?.status === 403 ||
        error?.response?.status === 404
    )
        return (
            <div className="flex items-center justify-center h-screen">
                <Loader />
            </div>
        );

    const attempt = quizAttemptData?.attempt;

    const quizQuestions = quizAttemptData?.data;
    const numberOfQuestions = quizQuestions?.length;

    const currentQuestionData = quizQuestions?.[currentQuestionNumber];

    function nextQuestion() {
        if (currentQuestionNumber < numberOfQuestions - 1) {
            setCurrentQuestionNumber(
                (currentQuestionNumber) => currentQuestionNumber + 1
            );
            setVisitedIterator((visitedIterator) => visitedIterator + 1);
            setSeenQuestions((prev) =>
                prev.includes(currentQuestionNumber + 2)
                    ? prev
                    : [...prev, currentQuestionNumber + 2]
            );
        }
    }

    function previousQuestion() {
        if (currentQuestionNumber > 0) {
            setCurrentQuestionNumber(
                (currentQuestionNumber) => currentQuestionNumber - 1
            );
            setVisitedIterator((visitedIterator) => visitedIterator - 1);
            setSeenQuestions((prev) =>
                prev.includes(currentQuestionNumber)
                    ? prev
                    : [...prev, currentQuestionNumber]
            );
        }
    }

    function getQuestion(questionNumber) {
        setCurrentQuestionNumber(questionNumber);
        setVisitedIterator(questionNumber + 1);
        setSeenQuestions((prev) =>
            prev.includes(questionNumber + 1)
                ? prev
                : [...prev, questionNumber + 1]
        );
    }

    function questionsState(formValues) {
        let countNull = 0;
        let countWithValue = 0;

        if (Object.keys(formValues).length !== 0) {
            for (const key in formValues) {
                if (Object.hasOwnProperty.call(formValues, key)) {
                    if (
                        formValues[key] !== null &&
                        formValues[key] !== undefined
                    ) {
                        countWithValue++;
                    } else {
                        countNull++;
                    }
                }
            }

            // Only update state if counts have changed
            if (
                countNull !== notAnsweredNumber ||
                countWithValue !== answeredNumber
            ) {
                setNotAnsweredNumber(countNull);
                setAnsweredNumber(countWithValue);
            }
        }
    }

    function onSubmitQuiz({ data, courseId, lectureId, quizId }) {
        // Check if data is defined before proceeding
        if (data) {
            const formattedData = [];
            Object.keys(data).forEach((key) => {
                if (key.startsWith("answer-")) {
                    const questionId = key.replace("answer-", "");
                    const chosenOptionId = data[key];
                    // Check if the chosen option is not null before adding it to formattedData
                    if (chosenOptionId !== null) {
                        formattedData.push({
                            question_id: parseInt(questionId, 10),
                            chosen_option_id: parseInt(chosenOptionId, 10),
                        });
                    }
                }
            });

            submitQuiz?.({
                answers: formattedData,
                courseId,
                lectureId,
                quizId,
                attemptId,
            });
            localStorage.removeItem("seenQuestions");
            setSeenQuestions([1]);
        } else {
            console.error("Data is undefined or null.");
        }
    }

    const now = new Date().getTime();
    const endTime =
        (attempt?.created_at &&
            new Date(attempt?.created_at).getTime() +
                (quizDetail?.data[0]?.attributes.quizDuration || 0) * 60000) ||
        now;

    return (
        <QuizContext.Provider
            value={{
                quizQuestions,
                seenQuestions,
                currentQuestionNumber,
                currentQuestionData,
                numberOfQuestions,
                answeredNumber,
                notAnsweredNumber,
                visitedNumber,
                quizDetail: quizDetail?.data[0],
                startTime: attempt?.created_at,
                endTime,
                quizTimeOut: error?.response?.status === 403 || endTime <= now,
                isSubmitting,
                nextQuestion,
                previousQuestion,
                onSubmitQuiz,
                getQuestion,
                questionsState,
            }}
        >
            {children}
        </QuizContext.Provider>
    );
}

function QuizStats() {
    const context = useContext(QuizContext);
    if (!context)
        throw new Error("use QuizStats must be used within a QuizProvider");
    return context;
}

export { QuizProvider, QuizStats };
